import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { aiAPI, localAgentAPI } from '../services/api';
import type { AIToolResult, LocalAgentRun } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tool_results?: AIToolResult[];
  thinking?: string;
}

interface UseAIOptions {
  taskId?: string;
  focusSessionId?: string;
  captureScope?: 'quick-inbox' | 'process-inbox';
}

function errorText(error: unknown): string {
  if (axios.isAxiosError(error) && typeof error.response?.data?.detail === 'string') return error.response.data.detail;
  return error instanceof Error ? error.message : 'The local AI connection stopped.';
}

export const useAI = ({ taskId, focusSessionId, captureScope }: UseAIOptions = {}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const scope = captureScope ? `capture:${captureScope}` : taskId || focusSessionId || 'web';
  const storageKey = `clazzo-pending-chat:${user?.id}:${scope}`;
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [model, setModel] = useState('local-claude-sonnet');
  const busy = useRef(false);
  const conversationRef = useRef<string | undefined>();
  const generation = useRef(0);

  const refreshData = useCallback(() => {
    for (const key of ['todos', 'events', 'comments', 'habits', 'documents', 'agent-conversations']) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [queryClient]);

  const displayConversation = useCallback(async (id: string, current: number) => {
    const conv = await aiAPI.getConversation(id);
    if (current !== generation.current) return;
    setConversationId(id);
    conversationRef.current = id;
    setModel(conv.model_provider?.startsWith('local-claude-') ? conv.model_provider : 'local-claude-sonnet');
    const calls = new Map<string, { name: string; arguments: Record<string, unknown> }>();
    const display: AIMessage[] = [];
    for (const [i, msg] of conv.messages.entries()) {
      for (const call of msg.tool_calls || []) calls.set(call.id, call);
      if (msg.role === 'tool') {
        const call = calls.get(msg.tool_call_id || '');
        let result: Record<string, unknown>;
        try { result = JSON.parse(msg.content || '{}'); }
        catch { result = { text: msg.content }; }
        display.push({ id: `tool-${id}-${i}`, role: 'assistant', content: '', timestamp: new Date(),
          tool_results: [{ tool: msg.tool_name || call?.name || 'Action', arguments: call?.arguments || {}, result, success: !result.error }] });
      } else if ((msg.role === 'user' || msg.role === 'assistant') && msg.content) {
        display.push({ id: `loaded-${id}-${i}`, role: msg.role, content: msg.content, timestamp: new Date(), thinking: msg.thinking || undefined });
      }
    }
    setMessages(display);
  }, []);

  const waitForRun = useCallback(async (initial: LocalAgentRun, current: number, requestKey: string) => {
    let run = initial;
    setConversationId(run.conversation_id || undefined);
    conversationRef.current = run.conversation_id || undefined;
    while (!run.result) {
      if (generation.current !== current) return null;
      setProgress(run.status === 'queued' ? 'Waiting for your Mac…' : 'Claude is working on your Mac…');
      await new Promise(resolve => setTimeout(resolve, 1500));
      if (generation.current !== current) return null;
      run = await localAgentAPI.getRun(run.id);
    }
    if (generation.current !== current) return null;
    refreshData(); // Failed runs can also contain committed task changes.
    if (run.conversation_id) await displayConversation(run.conversation_id, current);
    if (generation.current !== current) return null;
    if (sessionStorage.getItem(storageKey) === requestKey) sessionStorage.removeItem(storageKey);
    if (!run.result.success) setError(run.result.error || 'The local AI could not finish. Completed changes remain saved.');
    return run.result;
  }, [displayConversation, refreshData, storageKey]);

  const recover = useCallback(async () => {
    const requestKey = sessionStorage.getItem(storageKey);
    if (!requestKey || busy.current) return;
    busy.current = true;
    setIsLoading(true);
    setError(null);
    const current = generation.current;
    try {
      const run = await localAgentAPI.getRequest(requestKey);
      if (run) {
        if (run.conversation_id) await displayConversation(run.conversation_id, current);
        await waitForRun(run, current, requestKey);
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch (err) {
      if (current === generation.current) setError(`${errorText(err)} Reopen this chat to reconnect to the same request.`);
    } finally {
      if (current === generation.current) {
        busy.current = false;
        setIsLoading(false);
        setProgress('');
      }
    }
  }, [displayConversation, storageKey, waitForRun]);

  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    setMessages([]);
    setConversationId(undefined);
    conversationRef.current = undefined;
    setError(null);
    setIsLoading(false);
    setProgress('');
    void recover();
    return () => { generation.current += 1; };
  }, [recover]);

  const sendMessage = useCallback(async (message: string): Promise<AIMessage | null> => {
    if (!message.trim() || busy.current) return null;
    if (sessionStorage.getItem(storageKey)) {
      const current = generation.current;
      await recover();
      if (current !== generation.current) return null;
      setError(previous => [previous, 'No new request was submitted while checking the previous request. Review any saved changes before sending your draft.'].filter(Boolean).join(' '));
      return null;
    }
    const requestKey = crypto.randomUUID();
    const current = generation.current;
    const prefix = message.match(/^(opus|sonnet)\b\s+(.+)/is);
    const effectiveModel = prefix ? `local-claude-${prefix[1].toLowerCase()}` : model;
    const text = prefix ? prefix[2] : message;
    setModel(effectiveModel);
    setMessages(previous => [...(captureScope ? [] : previous), { id: requestKey, role: 'user', content: text, timestamp: new Date() }]);
    busy.current = true;
    setIsLoading(true);
    setProgress('Connecting to your Mac…');
    setError(null);
    sessionStorage.setItem(storageKey, requestKey);
    try {
      const run = await localAgentAPI.submit({ request_key: requestKey, message: text, model: effectiveModel,
        conversation_id: captureScope ? undefined : conversationRef.current, task_id: taskId, focus_session_id: focusSessionId });
      const result = await waitForRun(run, current, requestKey);
      return result?.success ? { id: run.id, role: 'assistant', content: result.content, timestamp: new Date(), tool_results: result.tool_results } : null;
    } catch (err) {
      if (current === generation.current) {
        // A rejected submission never started. An uncertain network result keeps
        // its ID so reopening the chat can recover without submitting it twice.
        if (axios.isAxiosError(err) && err.response && [400, 401, 403, 404, 409, 422, 503].includes(err.response.status)) {
          const accepted = await localAgentAPI.getRequest(requestKey).catch(() => undefined);
          if (accepted === null) sessionStorage.removeItem(storageKey);
        }
        setError(errorText(err));
      }
      return null;
    } finally {
      if (current === generation.current) {
        busy.current = false;
        setIsLoading(false);
        setProgress('');
      }
    }
  }, [captureScope, focusSessionId, model, recover, storageKey, taskId, waitForRun]);

  const loadConversation = useCallback(async (id: string) => {
    if (busy.current) return;
    busy.current = true;
    setIsLoading(true);
    setError(null);
    const current = generation.current;
    try {
      await displayConversation(id, current);
    } catch (err) {
      if (current === generation.current) setError(errorText(err));
    } finally {
      if (current === generation.current) {
        busy.current = false;
        setIsLoading(false);
      }
    }
  }, [displayConversation]);

  useEffect(() => {
    if (!taskId || sessionStorage.getItem(storageKey)) return;
    let cancelled = false;
    aiAPI.getConversations(taskId).then(conversations => {
      if (!cancelled && conversations.length) void loadConversation(conversations[0].id);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [taskId, storageKey, loadConversation]);

  const clearMessages = useCallback(() => {
    if (busy.current) return;
    setMessages([]);
    setConversationId(undefined);
    conversationRef.current = undefined;
    setError(null);
  }, []);

  return { messages, isLoading, progress, error, conversationId, model, setModel, sendMessage,
    loadConversation, clearMessages, clearError: () => setError(null), taskId };
};
